import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useChangeCurrentUserPassword } from 'src/features/profile/api/useChangeCurrentUserPassword';
import {
    ChangeCurrentUserPasswordFormSchemaValues,
    changeCurrentUserPasswordFormSchema,
} from 'src/features/profile/utils/formSchema';
import { passwordMessages } from 'src/features/profile/utils/messages';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { InputWithPassword } from 'src/registry/components/inputs/InputWithPassword';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

const ProfilePasswordForm = () => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<ChangeCurrentUserPasswordFormSchemaValues>({
        resolver: zodResolver(changeCurrentUserPasswordFormSchema),
        mode: 'onChange',
        defaultValues: { password: '', password_confirm: '' },
    });

    const { mutate, isPending } = useChangeCurrentUserPassword({
        onSuccess: () => {
            form.reset({ password: '', password_confirm: '' });
            toast({
                title: formatMessage(passwordMessages.passwordChangeSuccess),
                description: formatMessage(passwordMessages.passwordChangeSuccessDetails),
            });
        },
        onError: (e) => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: e?.response?.data?.errors?.[0]?.detail ?? '',
            });
        },
    });

    const onSubmit = (values: ChangeCurrentUserPasswordFormSchemaValues) => mutate(values);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(passwordMessages.newPassword)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(passwordMessages.newPasswordSubtitle)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <InputWithPassword type="password" autoComplete="new-password" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(passwordMessages.confirmNewPassword)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(passwordMessages.confirmNewPasswordSubtitle)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="password_confirm"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <InputWithPassword type="password" autoComplete="new-password" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button
                    type="submit"
                    disabled={!form.formState.isDirty || !form.formState.isValid || isPending}
                >
                    {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default ProfilePasswordForm;
